import { Box, Grid } from "@mui/material";
import { Typography } from "@powerdiary/components-library/lib";
import { DateTime } from "luxon";
import { AvailabilityMonthModel, IdNameItemModel, ProviderModel } from "types/generated/models";
import { BookingType, IProviderData, IGroupData } from "types/appointmentBooking";
import { IDay, ISlot } from "../AvailabilityShared.helpers";
import CalendarDay from "./CalendarDay";
import TimeSlots from "./TimeSlots";

import styles from "./CalendarContent.module.css";

interface ICalendarContentProps {
    weeks: Array<Array<IDay>>;
    availabilityData: AvailabilityMonthModel;
    selectedDate: DateTime | null;
    handleDateClick: (day: DateTime) => void;
    slotOnChange: (slot: ISlot, selectedProvider?: IdNameItemModel) => void;
    selectedSlot: ISlot | null;
    provider: IProviderData;
    availableProviders: ProviderModel[];
    bookingType: BookingType;
    defaultStripeAccount: string;
    onProviderSelect: (providerData: IProviderData) => void;
    onGroupSelect: (groupData: IGroupData) => void;
}

export default function CalendarContent({
    weeks,
    availabilityData,
    selectedDate,
    handleDateClick,
    slotOnChange,
    selectedSlot,
    provider,
    availableProviders,
    bookingType,
    defaultStripeAccount,
    onProviderSelect,
    onGroupSelect,
}: Readonly<ICalendarContentProps>) {
    const hasTimeEntries = availabilityData?.dayEntries?.some((day) => day.timeEntries && day.timeEntries.length > 0);

    return hasTimeEntries ? (
        <Box className={styles.calendar} data-test-id="availability_calendar">
            <Grid container data-test-id="day_headers">
                {["M", "T", "W", "T", "F", "S", "S"].map((day, index) => (
                    <Grid key={`${day}_${index}`} className={styles.dayHeaderContainer} size={{ xs: "grow" }}>
                        <Typography
                            variant="paragraphMediumSemiBold"
                            className={styles.dayHeader}
                            data-test-id={`day_header_${index}`}
                        >
                            {day}
                        </Typography>
                    </Grid>
                ))}
            </Grid>

            {weeks.map((week, weekIndex) => (
                <Box
                    key={`week-${week[0]?.date?.toISODate()}-${week[6]?.date?.toISODate()}`}
                    data-test-id={`week_${weekIndex}`}
                >
                    <Grid container data-test-id={`calendar_grid_${weekIndex}`}>
                        {week.map((day, dayIndex) => {
                            const isSelected = day.date && selectedDate?.equals(day.date);
                            return (
                                <CalendarDay
                                    key={`day-${day.date?.toISODate()}-${dayIndex}`}
                                    day={day}
                                    isSelected={!!isSelected}
                                    onClick={handleDateClick}
                                    dataTestIdSuffix={`${weekIndex}_${dayIndex}`}
                                />
                            );
                        })}
                    </Grid>

                    {selectedDate && week.some((day) => day.date?.equals(selectedDate)) && (
                        <TimeSlots
                            selectedDate={selectedDate}
                            weeks={weeks}
                            selectedSlot={selectedSlot}
                            slotOnChange={slotOnChange}
                            provider={provider}
                            availableProviders={availableProviders}
                            bookingType={bookingType}
                            defaultStripeAccount={defaultStripeAccount}
                            onProviderSelect={onProviderSelect}
                            onGroupSelect={onGroupSelect}
                        />
                    )}
                </Box>
            ))}
        </Box>
    ) : (
        <Box display="flex" justifyContent="center" p={3} data-test-id="no_appointments_available">
            <Typography variant="paragraphMediumRegular">No Appointments Available</Typography>
        </Box>
    );
}
